import type { PointerWithOffset } from '@/features/quiz/quiz-screen-graph-organizer'
import type { QuizState } from '@/lib/apis/types'
import type { TreatmentType } from '@/lib/apis/treatments'
import type { TreatmentQuizAttribution } from '@/lib/apis/treatments'
import { sanitizeTreatmentQuizAttribution } from './treatment-quiz-attribution.ts'

export const TREATMENT_QUIZ_ID_PARAM = 'quizId'

const STORAGE_VERSION = 2
const QUIZ_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

type StorageLike = Pick<Storage, 'getItem' | 'removeItem' | 'setItem'>

export type StoredTreatmentQuizPosition = Pick<
  PointerWithOffset,
  'pointer' | 'offset'
>

export type StoredTreatmentQuizProgress = {
  state: QuizState
  position: StoredTreatmentQuizPosition
  attribution?: TreatmentQuizAttribution | null
}

type StoredTreatmentQuizRecord = StoredTreatmentQuizProgress & {
  version: typeof STORAGE_VERSION
  treatmentType: TreatmentType
}

export function isTreatmentQuizId(value: unknown): value is string {
  return typeof value === 'string' && QUIZ_ID_PATTERN.test(value)
}

export function treatmentQuizStorageKey(
  treatmentType: TreatmentType,
  quizId: string,
) {
  return `nextclinic:treatment-quiz:${treatmentType}:${quizId}`
}

export function readTreatmentQuizProgress(
  storage: StorageLike,
  treatmentType: TreatmentType,
  quizId: string,
): StoredTreatmentQuizProgress | null {
  if (!isTreatmentQuizId(quizId)) return null

  try {
    const raw = storage.getItem(treatmentQuizStorageKey(treatmentType, quizId))
    if (!raw) return null

    const value: unknown = JSON.parse(raw)
    if (!isStoredTreatmentQuizRecord(value, treatmentType)) return null

    const attribution = sanitizeTreatmentQuizAttribution(value.attribution)

    return {
      state: value.state,
      position: value.position,
      ...(attribution ? { attribution } : {}),
    }
  } catch {
    return null
  }
}

export function writeTreatmentQuizProgress(
  storage: StorageLike,
  treatmentType: TreatmentType,
  quizId: string,
  progress: StoredTreatmentQuizProgress,
) {
  if (!isTreatmentQuizId(quizId)) return false

  try {
    const record: StoredTreatmentQuizRecord = {
      version: STORAGE_VERSION,
      treatmentType,
      ...progress,
      attribution: sanitizeTreatmentQuizAttribution(progress.attribution),
    }
    storage.setItem(
      treatmentQuizStorageKey(treatmentType, quizId),
      JSON.stringify(record),
    )
    return true
  } catch {
    return false
  }
}

export function removeTreatmentQuizProgress(
  storage: StorageLike,
  treatmentType: TreatmentType,
  quizId: string,
) {
  if (!isTreatmentQuizId(quizId)) return

  try {
    storage.removeItem(treatmentQuizStorageKey(treatmentType, quizId))
  } catch {
    // Storage cleanup must not interrupt a completed login.
  }
}

function isStoredTreatmentQuizRecord(
  value: unknown,
  treatmentType: TreatmentType,
): value is StoredTreatmentQuizRecord {
  if (!value || typeof value !== 'object') return false

  const record = value as Record<string, unknown>
  const state = record.state
  const position = record.position

  if (!state || typeof state !== 'object') return false
  const answers = (state as Record<string, unknown>).answers
  if (!answers || typeof answers !== 'object' || Array.isArray(answers)) {
    return false
  }

  if (!position || typeof position !== 'object') return false
  const storedPosition = position as Record<string, unknown>
  const pointer = storedPosition.pointer
  if (!pointer || typeof pointer !== 'object') return false
  const storedPointer = pointer as Record<string, unknown>

  return (
    (record.version === STORAGE_VERSION || record.version === 1) &&
    record.treatmentType === treatmentType &&
    typeof storedPosition.offset === 'number' &&
    Number.isInteger(storedPosition.offset) &&
    storedPosition.offset >= 0 &&
    typeof storedPointer.screenId === 'string' &&
    typeof storedPointer.groupIdx === 'number' &&
    Number.isInteger(storedPointer.groupIdx) &&
    storedPointer.groupIdx >= 0 &&
    typeof storedPointer.screenIdx === 'number' &&
    Number.isInteger(storedPointer.screenIdx) &&
    storedPointer.screenIdx >= 0
  )
}
